/**
 * File: useNotificationsBridgeController.ts
 * Path: apps/admin/src/modules/layout/hooks/useNotificationsBridgeController.ts
 * Description: Runs once inside the signed-in admin: refreshes this browser's push token, follows a tapped
 * notification to its page without a reload (sw.js posts `idk:navigate`), and keeps the app icon's badge at
 * the unread count where supported.
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { isAdminPath } from "@idk/config";
import { refreshPush } from "@/shared/services/pushClientService";
import { useInbox } from "@/shared/hooks/useNotificationsState";

let refreshed = false;

export const useNotificationsBridgeController = () => {
  const navigate = useNavigate();
  const { unread } = useInbox();

  useEffect(() => {
    if (refreshed) return;
    refreshed = true;
    void refreshPush();
  }, []);

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return undefined;
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: unknown; url?: unknown } | null;
      if (data?.type === "idk:navigate" && isAdminPath(data.url)) navigate(data.url);
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [navigate]);

  useEffect(() => {
    const nav = navigator as Navigator & {
      setAppBadge?: (n?: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };
    if (unread > 0) nav.setAppBadge?.(unread).catch(() => undefined);
    else nav.clearAppBadge?.().catch(() => undefined);
  }, [unread]);
};
